/**
 * Clause Properties API
 * RTK Query endpoints for clause properties management
 */

import { ClauseProperties } from '@/utils/clauseIdUtils';
import { rtkApi } from '../rtkApi';

export interface ClausePropertiesResponse {
  success: boolean;
  data: ClauseProperties[];
}

export interface ClausePropertiesSingleResponse {
  success: boolean;
  data: ClauseProperties;
}

export interface NegotiationSummary {
  totalClauses: number;
  originalClauses: number;
  proposedClauses: number;
  counteredClauses: number;
  acceptedClauses: number;
  rejectedClauses: number;
  ratifiedClauses: number;
  pendingClauses: number;
  urgentClauses: number;
}

export interface NegotiationSummaryResponse {
  success: boolean;
  data: NegotiationSummary;
}

export interface PendingNegotiationsResponse {
  success: boolean;
  data: { hasPending: boolean };
}

export interface ClauseStateChange {
  clauseId: string;
  hierarchicalId: string;
  contractId: string;
  oldStatus?: string;
  newStatus: string;
  changedBy: string;
  reason?: string;
  timestamp: string;
}

export interface ClauseHistoryResponse {
  success: boolean;
  data: ClauseStateChange[];
}

export const clausePropertiesApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get clause properties for a contract
    getClauseProperties: builder.query<
      ClausePropertiesResponse,
      {
        contractId: string;
        userRole?: string;
        contractRatified?: boolean;
      }
    >({
      query: ({ contractId, userRole, contractRatified }) => ({
        url: `/clauses/${contractId}/properties`,
        params: { userRole, contractRatified },
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'ClauseProperties', id: contractId },
        { type: 'ClauseProperties', id: 'LIST' },
      ],
    }),

    // Create clause properties
    createClauseProperties: builder.mutation<
      ClausePropertiesSingleResponse,
      {
        contractId: string;
        clauseId: string;
        properties: Partial<ClauseProperties>;
      }
    >({
      query: ({ contractId, clauseId, properties }) => ({
        url: `/clauses/${contractId}/properties`,
        method: 'POST',
        body: { clauseId, properties },
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: 'ClauseProperties', id: contractId },
        { type: 'ClauseProperties', id: 'LIST' },
      ],
    }),

    // Update clause properties
    updateClauseProperties: builder.mutation<
      ClausePropertiesSingleResponse,
      {
        contractId: string;
        clauseId: string;
        updates: Partial<ClauseProperties>;
      }
    >({
      query: ({ contractId, clauseId, updates }) => ({
        url: `/clauses/${contractId}/properties/${clauseId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: 'ClauseProperties', id: contractId },
        { type: 'ClauseProperties', id: 'LIST' },
      ],
    }),

    // Update clause status
    updateClauseStatus: builder.mutation<
      ClausePropertiesSingleResponse,
      {
        contractId: string;
        clauseId: string;
        newStatus: string;
        modifiedBy: string;
        reason?: string;
      }
    >({
      query: ({ contractId, clauseId, newStatus, modifiedBy, reason }) => ({
        url: `/clauses/${contractId}/${clauseId}/status`,
        method: 'PUT',
        body: { newStatus, modifiedBy, reason },
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: 'ClauseProperties', id: contractId },
        { type: 'ClauseProperties', id: 'LIST' },
      ],
    }),

    // Get clause history
    getClauseHistory: builder.query<
      ClauseHistoryResponse,
      {
        contractId: string;
        clauseId: string;
      }
    >({
      query: ({ contractId, clauseId }) => ({
        url: `/clauses/${contractId}/${clauseId}/history`,
      }),
      providesTags: (result, error, { contractId, clauseId }) => [
        { type: 'ClauseHistory', id: `${contractId}-${clauseId}` },
      ],
    }),

    // Get contract history
    getContractHistory: builder.query<
      ClauseHistoryResponse,
      {
        contractId: string;
      }
    >({
      query: ({ contractId }) => ({
        url: `/clauses/${contractId}/history`,
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'ClauseHistory', id: contractId },
      ],
    }),

    // Get actionable clauses
    getActionableClauses: builder.query<
      ClausePropertiesResponse,
      {
        contractId: string;
        userRole: string;
        action: string;
      }
    >({
      query: ({ contractId, userRole, action }) => ({
        url: `/clauses/${contractId}/actionable`,
        params: { userRole, action },
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'ClauseProperties', id: contractId },
        { type: 'ClauseProperties', id: 'LIST' },
      ],
    }),

    // Get clauses requiring attention
    getClausesRequiringAttention: builder.query<
      ClausePropertiesResponse,
      {
        contractId: string;
        userRole: string;
      }
    >({
      query: ({ contractId, userRole }) => ({
        url: `/clauses/${contractId}/attention`,
        params: { userRole },
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'ClauseProperties', id: contractId },
        { type: 'ClauseProperties', id: 'LIST' },
      ],
    }),

    // Get urgent clauses
    getUrgentClauses: builder.query<
      ClausePropertiesResponse,
      {
        contractId: string;
        userRole: string;
      }
    >({
      query: ({ contractId, userRole }) => ({
        url: `/clauses/${contractId}/urgent`,
        params: { userRole },
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'ClauseProperties', id: contractId },
        { type: 'ClauseProperties', id: 'LIST' },
      ],
    }),

    // Get negotiation summary
    getNegotiationSummary: builder.query<
      NegotiationSummaryResponse,
      {
        contractId: string;
      }
    >({
      query: ({ contractId }) => ({
        url: `/clauses/${contractId}/summary`,
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'NegotiationSummary', id: contractId },
      ],
    }),

    // Check pending negotiations
    hasPendingNegotiations: builder.query<
      PendingNegotiationsResponse,
      {
        contractId: string;
      }
    >({
      query: ({ contractId }) => ({
        url: `/clauses/${contractId}/pending`,
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'PendingNegotiations', id: contractId },
      ],
    }),

    // Initialize contract clauses
    initializeContractClauses: builder.mutation<
      ClausePropertiesResponse,
      {
        contractId: string;
        clauseIds: string[];
      }
    >({
      query: ({ contractId, clauseIds }) => ({
        url: `/clauses/${contractId}/initialize`,
        method: 'POST',
        body: { clauseIds },
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: 'ClauseProperties', id: contractId },
        { type: 'ClauseProperties', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetClausePropertiesQuery,
  useCreateClausePropertiesMutation,
  useUpdateClausePropertiesMutation,
  useUpdateClauseStatusMutation,
  useGetClauseHistoryQuery,
  useGetContractHistoryQuery,
  useGetActionableClausesQuery,
  useGetClausesRequiringAttentionQuery,
  useGetUrgentClausesQuery,
  useGetNegotiationSummaryQuery,
  useHasPendingNegotiationsQuery,
  useInitializeContractClausesMutation,
} = clausePropertiesApi;
